/**
 * Trasporto stampa locale.
 * Android: HTTP nativo (come Chrome sulla stampante) + socket ESC/POS.
 * PC web: QZ Tray. Mai stampa A4 di sistema.
 */
import { connectQz, qzPrintRaw, qzHttpRequest, qzSocketExchange, lastQzError } from "@/lib/qz";

export async function isNativePrint() {
  try {
    const { Capacitor } = await import("@capacitor/core");
    return !!Capacitor.isNativePlatform?.();
  } catch {
    return false;
  }
}

let tcpPlugin = null;
async function getTcpPrint() {
  if (tcpPlugin) return tcpPlugin;
  const native = await isNativePrint();
  if (!native) return null;
  const { registerPlugin } = await import("@capacitor/core");
  tcpPlugin = registerPlugin("TcpPrint");
  return tcpPlugin;
}

function lanExchange({ method, url, body, contentType, timeoutMs }) {
  const bridge = typeof window !== "undefined" ? window.VeltaLan : null;
  if (!bridge || typeof bridge.exchange !== "function") return null;
  try {
    const raw = String(bridge.exchange(method || "GET", url, body || "", contentType || "text/xml; charset=utf-8", String(timeoutMs || 4000)) || "");
    if (raw.startsWith("OK\n")) return { ok: true, response: raw.slice(3) };
    return { ok: false, error: raw.slice(0, 220) };
  } catch (e) {
    return { ok: false, error: String(e?.message || e) };
  }
}

function lanRaw({ host, port, data, timeoutMs, expectResponse }) {
  const bridge = typeof window !== "undefined" ? window.VeltaLan : null;
  if (!bridge || typeof bridge.raw !== "function") return null;
  try {
    const raw = String(bridge.raw(String(host || ""), String(port || 9100), String(data || ""), String(timeoutMs || 4000), expectResponse ? "1" : "0") || "");
    if (raw === "OK" || raw.startsWith("OK\n")) return { ok: true, response: raw.startsWith("OK\n") ? raw.slice(3) : "" };
    return { ok: false, error: raw.slice(0, 220) };
  } catch (e) {
    return { ok: false, error: String(e?.message || e) };
  }
}

function withTimeout(promise, ms, fallback) {
  let t;
  const timeout = new Promise((resolve) => {
    t = setTimeout(() => resolve(fallback), ms);
  });
  const wrapped = Promise.resolve(promise).catch((e) => ({ ok: false, error: String(e?.message || e) }));
  return Promise.race([wrapped, timeout]).finally(() => clearTimeout(t));
}

/** Test LAN: sul tablet GET HTTP come Chrome, altrimenti socket. */
export async function pingLanHost({ host, port, timeoutMs = 4000 }) {
  const ip = String(host || "").trim();
  const portN = Number(port) || 80;
  if (!ip) return { ok: false, reason: "no_host" };
  if (portN === 80 || portN === 8080) {
    const direct = lanExchange({
      method: "GET",
      url: `http://${ip}:${portN}/www/`,
      body: "",
      contentType: "text/plain",
      timeoutMs,
    });
    if (direct) {
      if (direct.ok && String(direct.response || "").length > 0) return { ok: true, via: "lan", response: direct.response };
      return { ok: false, reason: "send_failed", via: "lan", detail: direct.error || "pagina vuota" };
    }
  }
  const tcp = lanRaw({ host: ip, port: portN, data: "", timeoutMs: Math.min(4000, timeoutMs), expectResponse: false });
  if (tcp) {
    if (tcp.ok) return { ok: true, via: "lan" };
    return { ok: false, reason: "send_failed", via: "lan", detail: tcp.error || "socket chiusa" };
  }
  const plugin = await getTcpPrint();
  if (plugin && typeof plugin.ping === "function") {
    const r = await withTimeout(
      plugin.ping({ host: ip, port: portN, timeoutMs }),
      Math.max(12000, timeoutMs * 3 + 2000),
      { ok: false, error: `timeout ${timeoutMs}ms ${ip}:${portN}` }
    );
    if (r?.ok) return { ok: true, via: "android", response: r.response || "" };
    return { ok: false, reason: "send_failed", via: "android", detail: r?.error || "ping fallito" };
  }
  return sendRawTcp({ host: ip, port: portN, data: "", timeoutMs, expectResponse: false });
}

export async function sendRawTcp({ host, port, data, encoding = "ISO-8859-1", timeoutMs = 8000, expectResponse = false }) {
  const ip = String(host || "").trim();
  const portN = Number(port) || 9100;
  if (!ip) return { ok: false, reason: "no_host" };
  const payload = String(data ?? "");
  const fail = { ok: false, reason: "send_failed", detail: `timeout ${timeoutMs}ms verso ${ip}:${portN}` };
  const direct = lanRaw({ host: ip, port: portN, data: payload, timeoutMs: Math.min(8000, timeoutMs), expectResponse });
  if (direct) {
    if (direct.ok) return { ok: true, via: "lan", response: direct.response || "", printerHost: ip };
    return { ok: false, reason: "send_failed", via: "lan", detail: direct.error || "socket TCP fallita" };
  }

  const plugin = await getTcpPrint();
  if (plugin && typeof plugin.send === "function") {
    try {
      const r = await withTimeout(plugin.send({
        host: ip,
        port: portN,
        data: payload,
        encoding,
        timeoutMs,
        expectResponse,
      }), timeoutMs + 1500, { ok: false, error: fail.detail });
      if (r?.ok) return { ok: true, via: "android", response: r.response || "", printerHost: ip };
      return { ok: false, reason: "send_failed", via: "android", detail: r?.error || "socket TCP fallita" };
    } catch (e) {
      return { ok: false, reason: "send_failed", via: "android", detail: String(e?.message || e) };
    }
  }

  const qzOk = await connectQz();
  if (!qzOk) {
    return { ok: false, reason: "qz_offline", via: "web", detail: lastQzError || "QZ Tray non collegato. Allow sul popup, poi Riprova." };
  }
  try {
    if (expectResponse) {
      const text = encoding === "UTF-8"
        ? await qzHttpRequest(ip, portN, payload, { timeoutMs })
        : await qzSocketExchange(ip, portN, payload, { timeoutMs, encoding });
      return { ok: true, via: "qz", response: text || "" };
    }
    await qzPrintRaw({ ip, port: portN }, payload, "VELTA", encoding === "UTF-8" ? "UTF-8" : "CP858");
    return { ok: true, via: "qz" };
  } catch (e) {
    return { ok: false, reason: "send_failed", via: "qz", detail: String(e?.message || e) };
  }
}

export async function sendHttpToLan({ host, port = 80, path, body, contentType = "text/xml; charset=utf-8", timeoutMs = 15000 }) {
  const ip = String(host || "").trim();
  const pth = path.startsWith("/") ? path : `/${path}`;
  const payload = String(body || "");
  const direct = lanExchange({
    method: "POST",
    url: `http://${ip}:${Number(port) || 80}${pth}`,
    body: payload,
    contentType,
    timeoutMs: Math.min(8000, timeoutMs),
  });
  if (direct) {
    if (direct.ok) return { ok: true, via: "lan", response: direct.response || "" };
    return { ok: false, reason: "send_failed", via: "lan", detail: direct.error || "HTTP LAN fallita" };
  }
  const { utf8Len } = { utf8Len: (s) => {
    try { return new TextEncoder().encode(s).length; } catch { return String(s).length; }
  } };
  const raw = `POST ${pth} HTTP/1.1\r\nHost: ${ip}\r\nContent-Type: ${contentType}\r\nContent-Length: ${utf8Len(payload)}\r\nConnection: close\r\n\r\n${payload}`;
  return sendRawTcp({ host: ip, port, data: raw, encoding: "UTF-8", timeoutMs, expectResponse: true });
}

export async function fetchPrinterPage({ host, port = 80, path = "/www/", method = "GET", body = "", contentType = "text/xml; charset=utf-8", timeoutMs = 4000 }) {
  const ip = String(host || "").trim();
  const pth = path.startsWith("/") ? path : `/${path}`;
  const direct = lanExchange({
    method,
    url: `http://${ip}:${Number(port) || 80}${pth}`,
    body,
    contentType,
    timeoutMs: Math.min(4000, timeoutMs),
  });
  if (!direct) return { ok: false, error: "VeltaLan assente" };
  return direct.ok ? { ok: true, response: direct.response || "" } : { ok: false, error: direct.error || "lettura fallita" };
}

export async function probePrinter({ host, port, timeoutMs = 2500 }) {
  const plugin = await getTcpPrint();
  if (!plugin) return { ok: false, error: "non è l'APK (manca Capacitor)" };
  try {
    const { Capacitor } = await import("@capacitor/core");
    const listed = !!Capacitor.isPluginAvailable?.("TcpPrint");
    if (typeof plugin.probe !== "function") {
      return { ok: false, error: "APK senza metodo probe — reinstalla 1.0.12", listed };
    }
    const r = await withTimeout(
      plugin.probe({ host: String(host || "").trim(), port: Number(port) || 80, timeoutMs }),
      8000,
      { ok: false, error: "timeout probe" }
    );
    return { ...r, listed };
  } catch (e) {
    return { ok: false, error: String(e?.message || e) };
  }
}

export async function getLanInfo() {
  const plugin = await getTcpPrint();
  if (plugin && typeof plugin.lanInfo === "function") {
    try {
      return await plugin.lanInfo();
    } catch {
      return null;
    }
  }
  return null;
}

export async function installApkFromUrl(url) {
  const lan = typeof window !== "undefined" ? window.VeltaLan : null;
  if (lan && typeof lan.startInstall === "function") {
    const started = String(lan.startInstall(String(url || "")) || "");
    if (started.startsWith("ERR")) return { ok: false, error: started.replace(/^ERR\s*/, "") };
    if (started === "need_permission") {
      return { ok: false, error: "Abilita «Installa app sconosciute» per Velta One, poi premi di nuovo il pulsante." };
    }
    const deadline = Date.now() + 120000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 400));
      const st = String(lan.installStatus?.() || "");
      if (st === "prompt") return { ok: true };
      if (st === "need_permission") {
        return { ok: false, error: "Abilita «Installa app sconosciute» per Velta One, poi premi di nuovo il pulsante." };
      }
      if (st.startsWith("ERR")) return { ok: false, error: st.replace(/^ERR\s*/, "") };
    }
    return { ok: false, error: "timeout download APK" };
  }
  return { ok: false, error: "plugin assente" };
}

export function printChannelHint() {
  return "Da Chrome sul PC installa e avvia QZ Tray (qz.io). Sul tablet l'app parla HTTP/TCP in LAN come il browser, senza QZ.";
}
